import { ReadonlyMat4, ReadonlyVec3, vec3 } from 'gl-matrix';
import type { EventSource } from '~/domain/events/EventTypes';
import { WindowLevel } from '~/domain/patient/windowLevel';
import { DicomImageSlice } from '../../../domain/dicom/dicomImageSlice';
import { Image3D } from '../../../domain/patient/image3d';
import { gl } from '../../Engine';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { Grid } from '../common/Grid';

/** Max finite value in IEEE 754 half-precision float (R16F). */
const HALF_FLOAT_MAX = 65504;

/**
 * Whether the "float-linear unavailable" warning has already been emitted, so
 * a series of images does not repeat it.
 *
 * Only the WARNING is remembered, never the probe result. `getExtension` is
 * cheap and idempotent for a given context, whereas a cached answer would
 * outlive the context it was taken from if the engine is re-initialised on a
 * new one — and a texture format chosen from another context's capabilities
 * does not fail as an error, it renders as a blank image. The startup
 * capability check reports the extension's absence to the user
 * (`noFloatLinearFiltering`); this flag only keeps the console quiet.
 */
let warnedAboutMissingFloatLinear = false;

/**
 * {@link ImageObject} represents a 3D volume data.
 */
/**
 * What the renderer knows about one Z row of a progressively filled volume.
 *
 * Encoded as the byte stored in the R8 arrival-mask texture, so the values are
 * spread across the range rather than adjacent: the shader compares the
 * sampled float against midpoints, which stays exact under any filtering.
 */
export enum ArrivalState {
    /** Retrieval has not delivered this row yet; it may still arrive. */
    Pending = 0,
    /** Retrieval gave up on this row; it will never hold pixels. */
    Unavailable = 128,
    /** The row holds real pixels. */
    Arrived = 255,
}

export class ImageObject extends EnvironmentObject {
    /**
     * Source series UID
     */
    public readonly seriesInstanceUID: string;

    /**
     * The {@link WebGLTexture texture} in which the scan's texture data is stored.\
     * This may be null so unit tests don't require {@link WebGL2RenderingContext WebGL context}.
     */
    private readonly texture: WebGLTexture | null;

    /**
     * Grid size the texture storage was allocated at, captured once at
     * allocation. Slab uploads are validated against these rather than
     * against a live grid so a later grid change cannot silently turn an
     * in-range upload into an out-of-range one.
     */
    private readonly textureSizeX: number;
    private readonly textureSizeY: number;
    private readonly textureSizeZ: number;

    /**
     * Internal format chosen once, before allocation. WebGL cannot change
     * a texture's internal format after `texImage3D`, so this is fixed for
     * the lifetime of the object.
     */
    private readonly internalFormat: GLenum;

    /**
     * True when this is a wide-range (PET) image that had to fall back to
     * R16F because `OES_texture_float_linear` is unavailable: every upload
     * must be clamped to {@link HALF_FLOAT_MAX} first.
     */
    private readonly clampsToHalfFloat: boolean;

    /**
     * Per-Z record of which rows hold real pixels, as
     * {@link ArrivalState} bytes indexed by Z. Non-null only for a volume
     * that is still filling: a complete one has nothing to distinguish, and
     * its absence is what tells the renderer to skip the whole lookup.
     */
    private arrivalMask: Uint8Array | null = null;

    /**
     * GPU mirror of {@link arrivalMask} — an R8 texture of width
     * {@link textureSizeZ}, height 1 — so the fragment shader can tell an
     * absent voxel from a measured one. Released with {@link texture}.
     */
    private arrivalMaskTexture: WebGLTexture | null = null;

    /** Set by {@link delete}; guards uploads against use after free. */
    private deleted = false;

    public get windowLevel(): WindowLevel | undefined {
        return this.parent.windowLevel;
    }

    /**
     * Sets the volume's window/level, recording who set it: see
     * {@link Image3D.setWindowLevel}.
     */
    public setWindowLevel(windowLevel: WindowLevel, source: EventSource): void {
        // Window/level is transient display state, deliberately not routed
        // through a domain service: it is written from the interaction
        // path on every drag frame. See Image3D.setWindowLevel.
        this.parent.setWindowLevel(windowLevel, source);
    }

    private readonly _headFirstProne: boolean;

    public get headFirstProne(): boolean {
        return this._headFirstProne;
    }

    private _parent!: Image3D;
    public get parent(): Image3D {
        return this._parent;
    }

    /**
     * The grid defining the spatial properties of this image.
     * May be transformed (e.g., for secondary images with registration).
     */
    private _grid!: Grid;

    /**
     * Builds an {@link ImageObject} for a COMPLETE {@link Image3D} and
     * uploads the whole volume in one go.
     *
     * @param image3d The fully assembled source volume.
     * @param imageSlice A parsed slice of the series (modality, patient
     * orientation and window level are read off it).
     * @param transformation Optional world-space transformation (registration).
     */
    public static fromImage3D(
        image3d: Image3D,
        imageSlice: DicomImageSlice,
        transformation?: ReadonlyMat4
    ): ImageObject {
        const img = new ImageObject(
            image3d,
            imageSlice,
            image3d.dicomUid,
            transformation
        );

        // Uploads from a reference to the Image3D-owned Float32Array; no
        // copy is made. PET unsigned correction is already handled inside
        // Image3D.floatPixelData.
        img.uploadFullVolume();
        return img;
    }

    /**
     * Builds an {@link ImageObject} for a PARTIALLY filled {@link Image3D}
     * and uploads NO pixels.
     *
     * The texture is allocated at full grid size, so every voxel is
     * addressable immediately; un-uploaded voxels read back as zero. The
     * caller pushes rows in as they become valid with {@link uploadSlab}.
     * Uploading the rows that are valid at construction time (typically the
     * anchor slice) and then the remaining slabs converges on exactly the
     * same texture as a single {@link uploadFullVolume}.
     *
     * This object stays passive: it never schedules a redraw. The caller
     * invalidates the affected views after a slab lands.
     *
     * @param image3d The partially filled source volume. Its grid must
     * already describe the FULL volume; only pixel rows may be missing.
     * @param imageSlice A parsed slice of the series (see {@link fromImage3D}).
     * @param transformation Optional world-space transformation (registration).
     */
    public static fromPartialImage3D(
        image3d: Image3D,
        imageSlice: DicomImageSlice,
        transformation?: ReadonlyMat4
    ): ImageObject {
        return new ImageObject(
            image3d,
            imageSlice,
            image3d.dicomUid,
            transformation
        );
    }

    public constructor(
        image3d: Image3D,
        imageSlice: DicomImageSlice,
        seriesUID: string,
        transformation?: ReadonlyMat4
    ) {
        super();
        this.seriesInstanceUID = seriesUID;
        this._parent = image3d;
        this._grid = transformation
            ? image3d.grid.transform(transformation)
            : image3d.grid;

        // Storage is sized from the parent's (untransformed) grid, which is
        // the grid the pixel buffer is indexed by. A registration transform
        // moves the volume in world space; it does not resample it.
        const storageGrid = image3d.grid;
        this.textureSizeX = storageGrid.sizeX;
        this.textureSizeY = storageGrid.sizeY;
        this.textureSizeZ = storageGrid.sizeZ;

        // PET rescaled values can exceed the R16F max of 65504,
        // becoming Infinity in the texture. With LINEAR filtering,
        // Infinity propagates to neighbors as NaN, causing blank
        // rendering artifacts. Use R32F for wide-range images.
        //
        // The choice is made HERE, before allocation, and never revisited:
        // a texture's internal format is fixed by texImage3D. It depends
        // only on the modality, which is known from the first slice, so a
        // partially filled parent decides it exactly as a complete one does.
        const wideRange = image3d.hasWidePixelRange;
        const useFloat32 = wideRange && ImageObject.probeFloatLinearExtension();
        this.internalFormat = useFloat32 ? gl.R32F : gl.R16F;
        this.clampsToHalfFloat = wideRange && !useFloat32;

        this.texture = this.initializeTexture();

        // A volume that is still filling needs every Z row classified, so the
        // renderer can show an absent row as absent instead of as the zero the
        // buffer was allocated with. A complete volume has no absent rows and
        // carries no mask at all.
        if (image3d.isProgressivelyLoaded && !image3d.isComplete) {
            this.arrivalMask = new Uint8Array(this.textureSizeZ).fill(
                ArrivalState.Pending
            );
            this.arrivalMaskTexture = this.initializeArrivalMaskTexture();
        }

        this.setWindowLevel(image3d.windowLevelForFirstDisplay(), 'engine');
        this._headFirstProne = imageSlice.headFirstProne;
    }

    public get pixelSize(): ReadonlyVec3 {
        return this.grid.getSize();
    }

    /** Z size in pixels */
    public get sizeZ(): number {
        return this.grid.sizeZ;
    }

    /** Center of the center voxel in patient coordinates (mm). */
    public get centerVoxelCenter(): ReadonlyVec3 {
        return this.grid.toWorldSpace(
            vec3.fromValues(
                Math.floor(this.grid.sizeX / 2) + 0.5,
                Math.floor(this.grid.sizeY / 2) + 0.5,
                Math.floor(this.grid.sizeZ / 2) + 0.5
            )
        );
    }

    public get grid(): Grid {
        return this._grid;
    }

    /**
     * Updates the grid transformation in-place without recreating the object.
     * Use this when only the registration matrix has changed and the pixel
     * data on the GPU is still valid.
     *
     * @param transformation The new world-space transformation to apply to the parent grid.
     */
    public updateGridTransformation(transformation: ReadonlyMat4): void {
        this._grid = this.parent.grid.transform(transformation);
    }

    /**
     * Method to get the value of the image at a given patient coordinate.
     * @param patientCoords The patient coordinate at which to get the image value.
     * @returns The image value at the given patient coordinate or null if the coordinate is outside the bounds of the image.
     */
    public getImageObjectValueByCoordinate(
        patientCoords: ReadonlyVec3
    ): number | null {
        // Read the parent's live buffer rather than a reference captured at
        // upload time: it is the same array — a progressively filled volume
        // keeps one buffer identity for its whole life — and asking for it
        // here cannot go stale.
        const pixels = this._parent.floatPixelData;
        if (pixels.length === 0) {
            return null;
        }
        const index = this.grid.flatIndexAt(patientCoords);
        if (index === null) {
            return null;
        }
        // A row whose slice has not been retrieved still reads back as the
        // zero its buffer was allocated with, which on a CT is a plausible
        // measurement — "0 HU" is water, not "no data". The probe reports no
        // value there instead. The index is z-major, so the slice is its
        // quotient by the slice size.
        const sliceSize = this.grid.sizeX * this.grid.sizeY;
        if (!this._parent.hasArrivedAtZ(Math.floor(index / sliceSize))) {
            return null;
        }
        const imageObjectValue = pixels[index];
        if (isNaN(imageObjectValue) || imageObjectValue === undefined) {
            return null;
        }
        return imageObjectValue;
    }

    /**
     * One scroll step from `pos` along `dir` on this image's grid — the
     * displacement a wheel tick applies, snapped to the slice lattice where
     * one exists. See {@link Grid.scrollStep} for the rules.
     */
    public calculateStep(pos: ReadonlyVec3, dir: ReadonlyVec3): ReadonlyVec3 {
        return this.grid.scrollStep(pos, dir);
    }

    public delete(): boolean {
        gl.deleteTexture(this.texture);
        if (this.arrivalMaskTexture) {
            gl.deleteTexture(this.arrivalMaskTexture);
            this.arrivalMaskTexture = null;
        }
        this.deleted = true;
        return true;
    }

    /**
     * Creates the texture object AND allocates its full-grid storage.
     *
     * Allocation happens exactly once, here, with `null` data: the format
     * and extents are fixed from this point on and every later write is a
     * `texSubImage3D` into it. No pixel data is needed, which is what lets
     * a partially filled parent produce a usable object.
     *
     * Freshly allocated storage reads back as zero.
     */
    private initializeTexture(): WebGLTexture {
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(`Unable to create WebGLTexture for ImageObject.`);
        }
        gl.bindTexture(gl.TEXTURE_3D, texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

        gl.texImage3D(
            gl.TEXTURE_3D,
            0,
            this.internalFormat,
            this.textureSizeX,
            this.textureSizeY,
            this.textureSizeZ,
            0,
            gl.RED,
            gl.FLOAT,
            null
        );
        return texture;
    }

    /**
     * Whether the current context can LINEAR-filter R32F textures, which
     * is what makes R32F usable for a wide-range (PET) image.
     */
    private static probeFloatLinearExtension(): boolean {
        const extension: OES_texture_float_linear | null = gl.getExtension(
            'OES_texture_float_linear'
        );
        if (!extension && !warnedAboutMissingFloatLinear) {
            warnedAboutMissingFloatLinear = true;
            console.warn(
                'OES_texture_float_linear not available. ' +
                    'PET values above ' +
                    HALF_FLOAT_MAX +
                    ' will be clamped (reduced precision).'
            );
        }
        return extension !== null;
    }

    /** Number of voxels in one Z row of the allocated texture. */
    private get sliceVoxelCount(): number {
        return this.textureSizeX * this.textureSizeY;
    }

    /**
     * Uploads the entire volume from the parent's pixel buffer.
     *
     * Equivalent to `uploadSlab(0, sizeZ)`; kept as a named method because
     * "the image is complete, push all of it" is the call the non-streaming
     * path makes and reads better than a slab of the whole thing.
     */
    public uploadFullVolume(): void {
        this.uploadSlab(0, this.textureSizeZ);
    }

    /**
     * Uploads the Z rows `[zStart, zStart + zCount)` of the parent's pixel
     * buffer into the already-allocated texture.
     *
     * The caller guarantees those rows hold valid pixel data; uploading them
     * is what records them as arrived in the mask a still-filling volume
     * carries. Rows outside the requested range are left exactly as they were
     * (zero, and still pending, if never uploaded).
     *
     * On the R16F and R32F paths the slab is passed to WebGL as a
     * `subarray` view of the parent buffer — no copy. Only the
     * clamp-to-half-float PET fallback allocates, and then only for the
     * slab being uploaded, never for the whole volume.
     *
     * Passive by design: no view is invalidated and no redraw is scheduled.
     *
     * @param zStart Index of the first Z row to upload, in voxels.
     * @param zCount Number of Z rows to upload. Zero is a documented no-op
     * (an empty arrival batch is a legitimate state, not an error), so
     * callers need no special case for it.
     * @throws If the object has been deleted, if the range is not integral,
     * if it falls outside the allocated texture, or if the parent's buffer
     * is too short to cover it. Out-of-range indices are never clamped:
     * a clamped upload would write real pixels to the wrong slice.
     */
    public uploadSlab(zStart: number, zCount: number): void {
        if (this.deleted) {
            throw new Error(`ImageObject.uploadSlab called after delete().`);
        }
        this.assertSlabRange('uploadSlab', zStart, zCount);
        if (zCount === 0) {
            return;
        }
        if (!this.texture) {
            throw new Error(
                `ImageObject.uploadSlab has no texture to upload into.`
            );
        }

        const source = this.parent.floatPixelData;

        const offset = zStart * this.sliceVoxelCount;
        const voxelCount = zCount * this.sliceVoxelCount;
        if (source.length < offset + voxelCount) {
            throw new Error(
                `ImageObject.uploadSlab needs ${offset + voxelCount} voxels ` +
                    `for rows [${zStart}, ${zStart + zCount}) but the ` +
                    `source buffer holds ${source.length}.`
            );
        }

        let uploadData = source.subarray(offset, offset + voxelCount);
        if (this.clampsToHalfFloat) {
            // Clamp to half-float range to prevent Inf/NaN. Only the slab
            // is copied; clamping the whole volume per slab would make a
            // streamed load quadratic in allocation and copy cost.
            const clamped = new Float32Array(voxelCount);
            for (let i = 0; i < voxelCount; i++) {
                clamped[i] = Math.min(uploadData[i], HALF_FLOAT_MAX);
            }
            uploadData = clamped;
        }

        gl.bindTexture(gl.TEXTURE_3D, this.texture);
        // No pixelStorei needed: RED/FLOAT rows are 4 bytes per voxel, so
        // they are a multiple of 4 whatever UNPACK_ALIGNMENT is set to, and
        // the slab is a tightly packed view (no ROW_LENGTH / SKIP_* use).
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            0,
            zStart,
            this.textureSizeX,
            this.textureSizeY,
            zCount,
            gl.RED,
            gl.FLOAT,
            uploadData
        );

        // These rows now hold real pixels, which is the one fact the renderer
        // cannot read off the volume itself: an unarrived row and a row of
        // genuine zeros are the same bytes.
        this.writeArrivalMask(zStart, zCount, ArrivalState.Arrived);
    }

    public bindTexture(): void {
        gl.bindTexture(gl.TEXTURE_3D, this.texture);
    }

    /**
     * True while this volume can still show rows that hold no pixels. False
     * for a complete volume, which needs no per-row lookup at all.
     */
    public get hasArrivalMask(): boolean {
        return this.arrivalMaskTexture !== null;
    }

    /**
     * Binds the arrival mask to the currently active texture unit. Only
     * meaningful while {@link hasArrivalMask} is true.
     */
    public bindArrivalMask(): void {
        gl.bindTexture(gl.TEXTURE_2D, this.arrivalMaskTexture);
    }

    /**
     * Records that the Z rows `[zStart, zStart + zCount)` will never hold
     * pixels, so the renderer can distinguish them from rows that are merely
     * still in flight.
     *
     * Validated exactly as {@link uploadSlab} is, and a no-op on a complete
     * volume, which carries no mask.
     *
     * @param zStart Index of the first Z row written off, in voxels.
     * @param zCount Number of Z rows. Zero is a documented no-op.
     * @throws If the object has been deleted, if the range is not integral,
     * or if it falls outside the allocated texture depth.
     */
    public markSlabUnavailable(zStart: number, zCount: number): void {
        if (this.deleted) {
            throw new Error(
                `ImageObject.markSlabUnavailable called after delete().`
            );
        }
        this.assertSlabRange('markSlabUnavailable', zStart, zCount);
        if (zCount === 0) {
            return;
        }
        this.writeArrivalMask(zStart, zCount, ArrivalState.Unavailable);
    }

    /**
     * Creates the R8 arrival-mask texture and allocates its full-depth
     * storage, all rows pending. One texel per Z row.
     */
    private initializeArrivalMaskTexture(): WebGLTexture {
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(
                `Unable to create arrival-mask WebGLTexture for ImageObject.`
            );
        }

        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, 0);

        // NEAREST throughout: the mask is a classification, not a signal, and
        // interpolating between two states would invent a third.
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        // A single-byte-per-texel row is not a multiple of the default
        // 4-byte unpack alignment, so a volume whose depth is not a multiple
        // of four would be read from the wrong offsets without this.
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.R8,
            this.textureSizeZ,
            1,
            0,
            gl.RED,
            gl.UNSIGNED_BYTE,
            this.arrivalMask
        );
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
        gl.bindTexture(gl.TEXTURE_2D, null);

        return texture;
    }

    /** Writes one state across a Z range, in the buffer and on the GPU. */
    private writeArrivalMask(
        zStart: number,
        zCount: number,
        state: ArrivalState
    ): void {
        const mask = this.arrivalMask;
        if (!mask || !this.arrivalMaskTexture) {
            return;
        }

        mask.fill(state, zStart, zStart + zCount);

        gl.bindTexture(gl.TEXTURE_2D, this.arrivalMaskTexture);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texSubImage2D(
            gl.TEXTURE_2D,
            0,
            zStart,
            0,
            zCount,
            1,
            gl.RED,
            gl.UNSIGNED_BYTE,
            mask.subarray(zStart, zStart + zCount)
        );
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
        gl.bindTexture(gl.TEXTURE_2D, null);
    }

    /**
     * Validates a Z range against the allocated texture depth. Out-of-range
     * indices are never clamped: a clamped write would describe the wrong
     * slice.
     */
    private assertSlabRange(
        operation: string,
        zStart: number,
        zCount: number
    ): void {
        if (!Number.isInteger(zStart) || !Number.isInteger(zCount)) {
            throw new Error(
                `ImageObject.${operation} requires integer voxel indices, ` +
                    `got zStart=${zStart}, zCount=${zCount}.`
            );
        }
        if (zStart < 0 || zCount < 0 || zStart + zCount > this.textureSizeZ) {
            throw new Error(
                `ImageObject.${operation} range [${zStart}, ${
                    zStart + zCount
                }) is outside the allocated texture depth ` +
                    `${this.textureSizeZ}.`
            );
        }
    }
}
